import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Link from '@/components/Link';
import uniqueKey from '@/helpers/uniqueKey';
import TestimonialSlidePrevButton from './TestimonialSlidePrevButton';
import TestimonialSlideNextButton from './TestimonialSlideNextButton';
import TestimonialSlideLogoWrapper from './TestimonialSlideLogoWrapper';

const defaultProps = {};

const propTypes = {
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      logo: PropTypes.node.isRequired,
      bgColor: PropTypes.string.isRequired,
      testimonial: PropTypes.string.isRequired,
      who: PropTypes.string.isRequired,
      whoPosition: PropTypes.string.isRequired,
      partner: PropTypes.string.isRequired,
      partnerLink: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default function TestimonialSlides({ testimonials }) {
  const [active, setActive] = useState(0);

  const goPrev = useCallback(() => {
    setActive((ix) => {
      if (ix > 0) {
        return ix - 1;
      }
      return 0;
    });
  }, []);

  const goNext = useCallback(() => {
    setActive((ix) => {
      if (ix < 1) {
        return ix + 1;
      }
      return 0;
    });
  }, []);

  const totalSlides = testimonials.length;
  const translateValue =
    active === 0 ? '0%' : `-${(active * 100) / totalSlides}%`;

  return (
    <div className="rounded-lg xl:rounded-xl bg-white shadow-lg lg:shadow-2xl relative w-full">
      <TestimonialSlidePrevButton onClick={goPrev} />
      <TestimonialSlideNextButton onClick={goNext} />
      <div className="grid grid-cols-1 lg:grid-cols-12 w-full overflow-hidden relative z-10">
        <figure className="lg:col-span-4 overflow-hidden flex h-52 lg:h-auto rounded-t-lg lg:rounded-t-none lg:rounded-l-lg xl:rounded-l-xl">
          <ul className="list-none flex relative w-full transition transform">
            {testimonials.map((t, index) => (
              <TestimonialSlideLogoWrapper
                key={uniqueKey(`testimonial-logo-${index}`)}
                isActive={active === index}
                bgColor={t.bgColor}
              >
                {t.logo}
              </TestimonialSlideLogoWrapper>
            ))}
          </ul>
        </figure>
        <div className="lg:col-span-8 overflow-hidden">
          <ul
            style={{
              transform: `translateX(${translateValue})`,
              width: '200%',
            }}
            className="flex transition-transform duration-500 ease-in-out"
          >
            {testimonials.map((t, index) => (
              <li
                key={uniqueKey(`testimonial-${index}`)}
                className="p-10 lg:py-20 lg:px-20"
                style={{ width: '100%' }}
              >
                <div className="flex flex-col space-y-6">
                  <blockquote>
                    <svg
                      width="45"
                      height="36"
                      className="mb-5 fill-current text-gray-200"
                    >
                      <path d="M13.415.001C6.07 5.185.887 13.681.887 23.041c0 7.632 4.608 12.096 9.936 12.096 5.04 0 8.784-4.032 8.784-8.784 0-4.752-3.312-8.208-7.632-8.208-.864 0-2.016.144-2.304.288.72-4.896 5.328-10.656 9.936-13.536L13.415.001zm24.768 0c-7.2 5.184-12.384 13.68-12.384 23.04 0 7.632 4.608 12.096 9.936 12.096 4.896 0 8.784-4.032 8.784-8.784 0-4.752-3.456-8.208-7.776-8.208-.864 0-1.872.144-2.16.288.72-4.896 5.184-10.656 9.792-13.536L38.183.001z" />
                    </svg>
                    <p className="font-medium text-gray-800 text-lg lg:text-xl">
                      {t.testimonial}
                    </p>
                  </blockquote>
                  <div className="flex flex-col space-y-1">
                    <h5 className="text-gray-900 font-semibold text-lg">
                      {t.who}
                    </h5>
                    <p className="text-gray-500">
                      {t.whoPosition}&nbsp;
                      <Link
                        className="text-gray-800 font-medium"
                        href={t.partnerLink}
                      >
                        {t.partner}
                      </Link>
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

TestimonialSlides.defaultProps = defaultProps;
TestimonialSlides.propTypes = propTypes;
